'use client';

/**
 * SystemObeyaWidget - Obeya analytical dashboard
 *
 * For module_summary: renders a full dashboard with KPI stat cards,
 * sortable module table, productivity chart, and team comparison.
 * For other views: renders a generic sortable data table.
 *
 * Components ported from code-intelligence-hub, wired to the
 * live BFF endpoint at /docs/arch/obeya/{view}.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain system_obeya
 */

import { useState, useEffect, ReactNode } from 'react';
import {
  Target,
  Loader2,
  XCircle,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Code2,
  GitBranch,
  GitCommit,
  Sparkles,
  BookOpen,
  Clock,
  FileCode,
  Layers,
  Users,
  User,
  Zap,
  DollarSign,
  Cpu,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ObeyaViewResponse {
  view: string;
  timestamp: string;
  run_id?: string | null;
  data: Record<string, unknown>[];
  count: number;
  totals?: Record<string, unknown> | null;
  config?: Record<string, unknown> | null;
}

interface ColumnDef {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  format?: 'number' | 'percent' | 'currency' | 'duration' | 'text' | 'status';
}

// ---------------------------------------------------------------------------
// Generic view column configs (non-module_summary)
// ---------------------------------------------------------------------------

const GAP_ANALYSIS_COLUMNS: ColumnDef[] = [
  { key: 'package', label: 'Package', align: 'left', format: 'text' },
  { key: 'display_name', label: 'Display Name', align: 'left', format: 'text' },
  { key: 'function_points', label: 'FP', align: 'right', format: 'number' },
  { key: 'quality_score', label: 'Quality', align: 'right', format: 'number' },
  { key: 'test_count', label: 'Tests', align: 'right', format: 'number' },
  { key: 'status', label: 'Status', align: 'center', format: 'status' },
];

const VALUE_MAP_COLUMNS: ColumnDef[] = [
  { key: 'package', label: 'Package', align: 'left', format: 'text' },
  { key: 'display_name', label: 'Display Name', align: 'left', format: 'text' },
  { key: 'function_points', label: 'FP', align: 'right', format: 'number' },
  { key: 'quality_score', label: 'Quality', align: 'right', format: 'number' },
  { key: 'maintainability', label: 'Maintain', align: 'right', format: 'number' },
  { key: 'compliance_score', label: 'Compliance', align: 'right', format: 'number' },
  { key: 'analysis_cost', label: 'Cost', align: 'right', format: 'currency' },
  { key: 'test_count', label: 'Tests', align: 'right', format: 'number' },
];

const DEBT_DENSITY_COLUMNS: ColumnDef[] = [
  { key: 'package', label: 'Package', align: 'left', format: 'text' },
  { key: 'display_name', label: 'Display Name', align: 'left', format: 'text' },
  { key: 'function_points', label: 'FP', align: 'right', format: 'number' },
  { key: 'finding_count', label: 'Findings', align: 'right', format: 'number' },
  { key: 'findings_per_fp', label: 'Findings/FP', align: 'right', format: 'number' },
];

const RUN_HISTORY_COLUMNS: ColumnDef[] = [
  { key: 'run_id', label: 'Run ID', align: 'left', format: 'text' },
  { key: 'timestamp', label: 'Timestamp', align: 'left', format: 'text' },
  { key: 'model', label: 'Model', align: 'left', format: 'text' },
  { key: 'total_nodes', label: 'Nodes', align: 'right', format: 'number' },
  { key: 'total_findings', label: 'Findings', align: 'right', format: 'number' },
  { key: 'total_cost', label: 'Cost', align: 'right', format: 'currency' },
  { key: 'packages_analyzed', label: 'Packages', align: 'right', format: 'number' },
];

const VIEW_COLUMNS: Record<string, ColumnDef[]> = {
  gap_analysis: GAP_ANALYSIS_COLUMNS,
  value_map: VALUE_MAP_COLUMNS,
  debt_density: DEBT_DENSITY_COLUMNS,
  run_history: RUN_HISTORY_COLUMNS,
};

const VIEW_LABELS: Record<string, string> = {
  summary: 'Summary',
  module_summary: 'Module Summary',
  gap_analysis: 'Gap Analysis',
  value_map: 'Value Map',
  debt_density: 'Debt Density',
  high_cc_functions: 'Complexity Hotspots',
  cost_breakdown: 'Cost Breakdown',
  run_history: 'Run History',
  history: 'Run History',
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatCell(value: unknown, format: ColumnDef['format']): string {
  if (value == null) return '-';
  switch (format) {
    case 'number':
      return typeof value === 'number'
        ? value.toLocaleString(undefined, { maximumFractionDigits: 1 })
        : String(value);
    case 'percent':
      return typeof value === 'number' ? `${value}%` : String(value);
    case 'currency':
      return typeof value === 'number'
        ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : String(value);
    case 'duration':
      return typeof value === 'number' ? `${Math.round(value)}s` : String(value);
    default:
      return String(value);
  }
}

function num(val: unknown, fallback = 0): number {
  return typeof val === 'number' ? val : fallback;
}

// ---------------------------------------------------------------------------
// StatCard
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  subValue,
  icon,
}: {
  label: string;
  value: string | number;
  subValue?: string;
  icon: ReactNode;
}) {
  return (
    <div className="glass-card rounded-xl p-6 group hover:border-primary/30 transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className="p-2.5 rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
          {icon}
        </div>
        {subValue && (
          <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-md">
            {subValue}
          </span>
        )}
      </div>
      <div className="stat-value">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <p className="text-sm text-muted-foreground mt-1 font-medium">{label}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QualityBadge / DeclarativeBadge
// ---------------------------------------------------------------------------

function QualityBadge({ quality }: { quality: number }) {
  const color =
    quality >= 95
      ? 'bg-success/20 text-success'
      : quality >= 90
        ? 'bg-primary/20 text-primary'
        : 'bg-warning/20 text-warning';
  return <span className={`metric-badge ${color}`}>{quality}%</span>;
}

function DeclarativeBadge({ percent }: { percent: number }) {
  const color =
    percent >= 85
      ? 'bg-success/20 text-success'
      : percent >= 60
        ? 'bg-info/20 text-info'
        : 'bg-muted text-muted-foreground';
  return <span className={`metric-badge ${color}`}>{percent}%</span>;
}

// ---------------------------------------------------------------------------
// ModuleTable (sortable, ported from code-intelligence-hub)
// ---------------------------------------------------------------------------

type ModuleSortKey =
  | 'package'
  | 'files_count'
  | 'sloc'
  | 'commits'
  | 'function_points'
  | 'story_count'
  | 'declarative_percentage'
  | 'quality_score'
  | 'test_coverage';

function ModuleTable({ rows }: { rows: Record<string, unknown>[] }) {
  const [sortKey, setSortKey] = useState<ModuleSortKey>('sloc');
  const [sortAsc, setSortAsc] = useState(false);

  const sorted = [...rows].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortAsc ? aVal - bVal : bVal - aVal;
    }
    return sortAsc
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : String(bVal ?? '').localeCompare(String(aVal ?? ''));
  });

  const handleSort = (key: ModuleSortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const SortIcon = ({ column }: { column: ModuleSortKey }) => {
    if (sortKey !== column) return null;
    return sortAsc ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <div className="p-6 border-b border-border/50">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Layers className="w-5 h-5 text-primary" />
          Module Metrics
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Click column headers to sort
        </p>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border/50">
              <TableHead
                className="cursor-pointer hover:text-primary transition-colors"
                onClick={() => handleSort('package')}
              >
                Module <SortIcon column="package" />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:text-primary transition-colors text-right"
                onClick={() => handleSort('files_count')}
              >
                Files <SortIcon column="files_count" />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:text-primary transition-colors text-right"
                onClick={() => handleSort('sloc')}
              >
                SLOC <SortIcon column="sloc" />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:text-primary transition-colors text-right"
                onClick={() => handleSort('commits')}
              >
                Commits <SortIcon column="commits" />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:text-primary transition-colors text-right"
                onClick={() => handleSort('function_points')}
              >
                FP <SortIcon column="function_points" />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:text-primary transition-colors text-right"
                onClick={() => handleSort('story_count')}
              >
                Stories <SortIcon column="story_count" />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:text-primary transition-colors text-right"
                onClick={() => handleSort('declarative_percentage')}
              >
                Declarative <SortIcon column="declarative_percentage" />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:text-primary transition-colors text-right"
                onClick={() => handleSort('quality_score')}
              >
                Quality <SortIcon column="quality_score" />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:text-primary transition-colors text-right"
                onClick={() => handleSort('test_coverage')}
              >
                Test % <SortIcon column="test_coverage" />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((mod) => (
              <TableRow
                key={String(mod.package)}
                className="hover:bg-primary/5 border-border/30 transition-colors"
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <FileCode className="w-4 h-4 text-muted-foreground" />
                    <span className="module-name">{String(mod.package)}</span>
                  </div>
                  {mod.description && (
                    <p className="text-xs text-muted-foreground mt-1 max-w-xs truncate">
                      {String(mod.description)}
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {num(mod.files_count)}
                </TableCell>
                <TableCell className="text-right font-mono text-sm font-medium text-primary">
                  {num(mod.sloc).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  <span className="inline-flex items-center gap-1 text-sm">
                    <GitCommit className="w-3 h-3 text-muted-foreground" />
                    {num(mod.commits)}
                  </span>
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {num(mod.function_points)}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {num(mod.story_count)}
                </TableCell>
                <TableCell className="text-right">
                  <DeclarativeBadge percent={num(mod.declarative_percentage)} />
                </TableCell>
                <TableCell className="text-right">
                  <QualityBadge quality={num(mod.quality_score)} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${num(mod.test_coverage)}%` }}
                      />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground w-10 text-right">
                      {num(mod.test_coverage)}%
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProductivityChart (recharts horizontal bar)
// ---------------------------------------------------------------------------

function ProductivityChart({ rows }: { rows: Record<string, unknown>[] }) {
  const chartData = rows.map((d) => ({
    name: String(d.package ?? '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase()),
    shortName: String(d.package ?? '').split('_')[0],
    traditional: num(d.trad_hours),
    ai: num(d.ai_hours),
    saved: num(d.saved_hours),
  }));

  return (
    <div className="glass-card rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            AI Productivity Gains
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Traditional hours vs AI-assisted hours per module
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-muted-foreground/40" />
            Traditional
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-primary" />
            AI-Assisted
          </span>
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 0, right: 30, left: 0, bottom: 0 }}
          >
            <XAxis
              type="number"
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`}
            />
            <YAxis
              type="category"
              dataKey="shortName"
              stroke="hsl(var(--muted-foreground))"
              fontSize={11}
              tickLine={false}
              axisLine={false}
              width={90}
              tick={{ fill: 'hsl(var(--primary))', fontFamily: 'monospace' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}
              itemStyle={{ color: 'hsl(var(--muted-foreground))' }}
              formatter={(value: number, name: string) => [
                `${value.toLocaleString()} hours`,
                name === 'traditional' ? 'Traditional' : 'AI-Assisted',
              ]}
              labelFormatter={(label) =>
                chartData.find((d) => d.shortName === label)?.name || label
              }
            />
            <Bar
              dataKey="traditional"
              fill="hsl(var(--muted))"
              radius={[0, 4, 4, 0]}
              name="traditional"
            />
            <Bar
              dataKey="ai"
              fill="hsl(var(--primary))"
              radius={[0, 4, 4, 0]}
              name="ai"
            >
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill="hsl(var(--primary))" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TeamComparison
// ---------------------------------------------------------------------------

function TeamComparison({
  totals,
  config,
}: {
  totals: Record<string, unknown>;
  config?: Record<string, unknown> | null;
}) {
  const tradHours = num(totals.total_trad_hours);
  const aiHours = num(totals.total_ai_hours);
  const savedHours = num(totals.total_saved_hours);
  const tradCost = num(totals.trad_cost_usd);
  const aiCost = num(totals.ai_cost_usd);
  const savings = num(totals.savings_usd);
  const calendarDays = num(totals.calendar_days);
  const teamSize = num(config?.traditional_team_size, 8);
  const aiTeamSize = num(config?.ai_team_size, 1);
  const teamSaved = teamSize - aiTeamSize;

  const comparisons = [
    {
      label: 'Traditional Agile Team',
      icon: Users,
      team: `${teamSize} people`,
      days: `${calendarDays.toLocaleString()} calendar days`,
      hours: tradHours.toLocaleString(),
      cost: `$${tradCost.toLocaleString()}`,
      highlight: false,
    },
    {
      label: `${aiTeamSize} Person + AI`,
      icon: User,
      team: `${aiTeamSize} person`,
      days: `${calendarDays.toLocaleString()} calendar days`,
      hours: aiHours.toLocaleString(),
      cost: `$${aiCost.toLocaleString()}`,
      highlight: true,
    },
  ];

  return (
    <div className="glass-card rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Team Productivity Comparison
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Traditional team vs AI-augmented developer
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {comparisons.map((c) => (
          <div
            key={c.label}
            className={`rounded-xl p-5 border transition-all ${
              c.highlight
                ? 'border-primary/50 bg-primary/5 glow-effect'
                : 'border-border/50 bg-muted/30'
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`p-2 rounded-lg ${
                  c.highlight
                    ? 'bg-primary/20 text-primary'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                <c.icon className="w-5 h-5" />
              </div>
              <h3
                className={`font-semibold ${c.highlight ? 'text-primary' : ''}`}
              >
                {c.label}
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Users className="w-4 h-4" /> Team Size
                </span>
                <span className="font-medium">{c.team}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Duration
                </span>
                <span className="font-medium">{c.days}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Zap className="w-4 h-4" /> Total Hours
                </span>
                <span className="font-mono font-medium">{c.hours}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Est. Cost
                </span>
                <span
                  className={`font-mono font-medium ${c.highlight ? 'text-primary' : ''}`}
                >
                  {c.cost}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Savings Summary */}
      <div className="rounded-xl bg-primary/5 border border-primary/20 p-5">
        <div className="flex items-center gap-2 mb-4">
          <TrendingDown className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-primary">Total Savings</h3>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-2xl font-bold text-primary">-{teamSaved}</p>
            <p className="text-xs text-muted-foreground">Team members</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-primary">
              {savedHours.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Hours saved</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-primary">
              ${savings >= 1_000_000
                ? `${(savings / 1_000_000).toFixed(2)}M`
                : savings.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Cost savings</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Module Summary Dashboard (full layout)
// ---------------------------------------------------------------------------

function ModuleSummaryDashboard({
  data,
  onRefresh,
}: {
  data: ObeyaViewResponse;
  onRefresh: () => void;
}) {
  const totals = data.totals ?? {};
  const config = data.config;

  return (
    <div className="h-full overflow-auto scrollbar-thin">
      {/* Header bar */}
      <div className="sticky top-0 z-10 border-b border-border/50 bg-background/95 backdrop-blur-sm px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cpu className="w-5 h-5 text-primary" />
            <span className="font-semibold">Module Summary</span>
            <Badge variant="secondary" className="text-[10px] px-1.5">
              {data.count} modules
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {new Date(data.timestamp).toLocaleString()}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={onRefresh}
              className="h-7 w-7 p-0"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total SLOC"
            value={num(totals.total_sloc)}
            icon={<Code2 className="w-5 h-5" />}
            subValue={`${num(totals.total_files)} files`}
          />
          <StatCard
            label="Function Points"
            value={Math.round(num(totals.total_fp))}
            icon={<Sparkles className="w-5 h-5" />}
            subValue={`${num(totals.total_features)} features`}
          />
          <StatCard
            label="User Stories"
            value={num(totals.total_stories)}
            icon={<BookOpen className="w-5 h-5" />}
            subValue={`${num(totals.total_capabilities)} caps`}
          />
          <StatCard
            label="Hours Saved"
            value={Math.round(num(totals.total_saved_hours)).toLocaleString()}
            icon={<Clock className="w-5 h-5" />}
            subValue={`${num(totals.total_gain_pct)}% faster`}
          />
        </div>

        {/* Module Table */}
        <ModuleTable rows={data.data} />

        {/* Charts Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          <ProductivityChart rows={data.data} />
          <TeamComparison totals={totals} config={config} />
        </div>

        {/* Footer note */}
        <div className="text-center text-sm text-muted-foreground pb-4">
          <p>
            Infrastructure modules may require imperative programming for
            external system integration. Low declarative % reflects appropriate
            design choices.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary Dashboard (stat cards for high-level graph counts)
// ---------------------------------------------------------------------------

function SummaryDashboard({
  data,
  onRefresh,
}: {
  data: ObeyaViewResponse;
  onRefresh: () => void;
}) {
  const row = data.data[0] ?? {};

  return (
    <div className="h-full overflow-auto scrollbar-thin">
      <div className="sticky top-0 z-10 border-b border-border/50 bg-background/95 backdrop-blur-sm px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="w-5 h-5 text-primary" />
            <span className="font-semibold">Summary</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {new Date(data.timestamp).toLocaleString()}
            </span>
            <Button size="sm" variant="ghost" onClick={onRefresh} className="h-7 w-7 p-0">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Run info */}
        {row.run_id && (
          <div className="text-xs text-muted-foreground flex items-center gap-4">
            <span>Run: <code className="font-mono">{String(row.run_id)}</code></span>
            {row.model && <span>Model: {String(row.model)}</span>}
            {row.total_cost != null && <span>Cost: ${num(row.total_cost).toFixed(2)}</span>}
          </div>
        )}

        {/* Architecture counts */}
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Architecture</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Packages" value={num(row.packages)} icon={<Layers className="w-5 h-5" />} />
            <StatCard label="Capabilities" value={num(row.capabilities)} icon={<GitBranch className="w-5 h-5" />} />
            <StatCard label="Features" value={num(row.features)} icon={<Sparkles className="w-5 h-5" />} />
            <StatCard label="User Stories" value={num(row.stories)} icon={<BookOpen className="w-5 h-5" />} />
          </div>
        </div>

        {/* Quality counts */}
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Quality &amp; Testing</h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard label="Findings" value={num(row.findings)} icon={<Code2 className="w-5 h-5" />} />
            <StatCard label="Test Cases" value={num(row.test_cases)} icon={<FileCode className="w-5 h-5" />} />
            <StatCard
              label="Packages with Tests"
              value={num(row.packages_with_tests)}
              icon={<Zap className="w-5 h-5" />}
              subValue={`of ${num(row.packages)}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cost Breakdown Dashboard (per-module + per-stage economics)
// ---------------------------------------------------------------------------

function CostBreakdownDashboard({
  data,
  onRefresh,
}: {
  data: ObeyaViewResponse;
  onRefresh: () => void;
}) {
  const nested = data.data[0] ?? {};
  const modules = (nested.modules as Record<string, unknown>[]) ?? [];
  const stages = (nested.stages as Record<string, unknown>[]) ?? [];
  const totals = data.totals ?? {};
  const stageNames: Record<string, string> =
    (data.config?.stage_display_names as Record<string, string>) ?? {};

  return (
    <div className="h-full overflow-auto scrollbar-thin">
      <div className="sticky top-0 z-10 border-b border-border/50 bg-background/95 backdrop-blur-sm px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-primary" />
            <span className="font-semibold">Cost Breakdown</span>
            <Badge variant="secondary" className="text-[10px] px-1.5">
              {modules.length} modules
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {new Date(data.timestamp).toLocaleString()}
            </span>
            <Button size="sm" variant="ghost" onClick={onRefresh} className="h-7 w-7 p-0">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total Cost"
            value={`$${num(totals.grand_cost).toFixed(2)}`}
            icon={<DollarSign className="w-5 h-5" />}
            subValue={`${num(totals.module_count)} modules`}
          />
          <StatCard
            label="Total Tokens"
            value={num(totals.grand_tokens).toLocaleString()}
            icon={<Cpu className="w-5 h-5" />}
            subValue={`${num(totals.stage_count)} stages`}
          />
          <StatCard
            label="FP per Dollar"
            value={num(totals.fp_per_dollar).toLocaleString()}
            icon={<Sparkles className="w-5 h-5" />}
            subValue="efficiency"
          />
          <StatCard
            label="Total Duration"
            value={`${Math.round(num(totals.grand_duration))}s`}
            icon={<Clock className="w-5 h-5" />}
            subValue={`$${num(totals.cost_per_1k_sloc).toFixed(4)}/1k SLOC`}
          />
        </div>

        {/* Per-module table */}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border/50">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary" />
              Per-Module Cost
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-left">Module</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">Cost</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">Tokens</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">Duration</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">FP</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">SLOC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {modules.map((m, i) => (
                  <tr key={i} className="hover:bg-accent/30 transition-colors">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <FileCode className="w-4 h-4 text-muted-foreground" />
                        <span className="module-name">{String(m.display_name || m.module)}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      ${num(m.total_cost).toFixed(4)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {num(m.total_tokens).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {Math.round(num(m.duration))}s
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {num(m.function_points)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {num(m.sloc).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Per-stage table */}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border/50">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Per-Stage Cost
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-left">Stage</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">Cost</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">Tokens</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">Duration</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">Modules</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {stages.map((s, i) => (
                  <tr key={i} className="hover:bg-accent/30 transition-colors">
                    <td className="px-3 py-2 font-medium text-sm">
                      {stageNames[String(s.stage)] || String(s.stage)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      ${num(s.total_cost).toFixed(4)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {num(s.total_tokens).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {Math.round(num(s.duration))}s
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {num(s.module_count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Complexity Hotspots Dashboard (high CC modules + function detail)
// ---------------------------------------------------------------------------

function ComplexityHotspotsDashboard({
  data,
  onRefresh,
}: {
  data: ObeyaViewResponse;
  onRefresh: () => void;
}) {
  const nested = data.data[0] ?? {};
  const modules = (nested.modules as Record<string, unknown>[]) ?? [];
  const functions = (nested.functions as Record<string, unknown>[]) ?? [];
  const totals = data.totals ?? {};
  const config = data.config ?? {};

  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  const severityColor = (cc: number) => {
    const critical = num(config.critical, 30);
    const high = num(config.high, 20);
    if (cc >= critical) return 'bg-destructive/20 text-destructive';
    if (cc >= high) return 'bg-warning/20 text-warning';
    return 'bg-primary/20 text-primary';
  };

  return (
    <div className="h-full overflow-auto scrollbar-thin">
      <div className="sticky top-0 z-10 border-b border-border/50 bg-background/95 backdrop-blur-sm px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cpu className="w-5 h-5 text-primary" />
            <span className="font-semibold">Complexity Hotspots</span>
            <Badge variant="secondary" className="text-[10px] px-1.5">
              {num(totals.total_high_cc_functions)} functions
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {new Date(data.timestamp).toLocaleString()}
            </span>
            <Button size="sm" variant="ghost" onClick={onRefresh} className="h-7 w-7 p-0">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="High CC Functions"
            value={num(totals.total_high_cc_functions)}
            icon={<Cpu className="w-5 h-5" />}
            subValue={`of ${num(totals.total_functions)}`}
          />
          <StatCard
            label="Avg CC"
            value={num(totals.avg_cc)}
            icon={<TrendingUp className="w-5 h-5" />}
          />
          <StatCard
            label="Highest CC"
            value={num(totals.highest_cc)}
            icon={<TrendingUp className="w-5 h-5" />}
          />
          <StatCard
            label="Modules Affected"
            value={num(totals.modules_affected)}
            icon={<Layers className="w-5 h-5" />}
          />
        </div>

        {/* Module breakdown */}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border/50">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary" />
              Module Breakdown
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-left">Module</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">High CC</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">Total Fns</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">% High</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {modules.map((m, i) => {
                  const modId = String(m.package || m.display_name);
                  const isModSelected = selectedNode?.type === 'cc_module' && selectedNode?.id === modId;
                  return (
                    <tr
                      key={i}
                      className={cn(
                        'cursor-pointer transition-colors',
                        isModSelected ? 'bg-primary/10' : 'hover:bg-accent/30',
                      )}
                      onClick={() => {
                        const moduleFunctions = functions
                          .filter((f) => String(f.package) === String(m.package))
                          .map((f) => ({
                            function_name: f.function_name,
                            name: f.name,
                            cc_value: f.cc_value,
                            severity: f.severity,
                            location: f.location,
                            file_stem: f.file_stem,
                            suggestion: f.suggestion,
                            package: f.package,
                            package_display_name: f.package_display_name,
                          }));
                        selectNode({
                          id: modId,
                          label: String(m.display_name || m.package),
                          type: 'cc_module',
                          properties: {
                            package: m.package,
                            display_name: m.display_name,
                            high_cc_count: m.high_cc_count,
                            total_functions: m.total_functions,
                            high_cc_percentage: m.high_cc_percentage,
                            functions: moduleFunctions,
                          },
                        });
                      }}
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <FileCode className="w-4 h-4 text-muted-foreground" />
                          <span className="module-name">{String(m.display_name || m.package)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{num(m.high_cc_count)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{num(m.total_functions)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {m.high_cc_percentage != null ? `${num(m.high_cc_percentage)}%` : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Function detail */}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border/50">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Code2 className="w-5 h-5 text-primary" />
              Function Detail
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-left">Function</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-left">Module</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-right">CC</th>
                  <th className="px-3 py-2 text-xs font-semibold text-muted-foreground text-left">Severity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {functions.map((f, i) => {
                  const fnId = String(f.function_name || f.name);
                  const isSelected = selectedNode?.type === 'cc_function' && selectedNode?.id === fnId;
                  return (
                    <tr
                      key={i}
                      className={cn(
                        'cursor-pointer transition-colors',
                        isSelected ? 'bg-primary/10' : 'hover:bg-accent/30',
                      )}
                      onClick={() =>
                        selectNode({
                          id: fnId,
                          label: fnId,
                          type: 'cc_function',
                          properties: {
                            function_name: f.function_name,
                            name: f.name,
                            cc_value: f.cc_value,
                            severity: f.severity,
                            location: f.location,
                            file_stem: f.file_stem,
                            suggestion: f.suggestion,
                            package: f.package,
                            package_display_name: f.package_display_name,
                          },
                        })
                      }
                    >
                      <td className="px-3 py-2">
                        <code className="text-xs font-mono">{fnId}</code>
                        {f.location && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">{String(f.location)}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">{String(f.package_display_name || f.package)}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`metric-badge ${severityColor(num(f.cc_value))}`}>
                          {num(f.cc_value)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs capitalize">{String(f.severity ?? '-')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic Table (non-module_summary views)
// ---------------------------------------------------------------------------

function GenericTable({
  data,
  onRefresh,
}: {
  data: ObeyaViewResponse;
  onRefresh: () => void;
}) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  const columns = VIEW_COLUMNS[data.view] ?? [];
  const viewLabel = VIEW_LABELS[data.view] ?? data.view;

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  let rows = [...data.data];
  if (sortKey) {
    rows.sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortAsc ? aVal - bVal : bVal - aVal;
      }
      return sortAsc
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="border-b border-border/50 bg-muted/30 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-sm">{viewLabel}</span>
            <Badge variant="secondary" className="text-[10px] px-1.5">
              {data.count} rows
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {new Date(data.timestamp).toLocaleString()}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={onRefresh}
              className="h-7 w-7 p-0"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-muted/50 border-b border-border">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'px-3 py-2 text-xs font-semibold text-muted-foreground whitespace-nowrap cursor-pointer hover:text-foreground',
                    col.align === 'right' && 'text-right',
                    col.align === 'center' && 'text-center'
                  )}
                  onClick={() => handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {sortKey === col.key && (
                      <ArrowUpDown className="w-3 h-3" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/30">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-accent/30 transition-colors">
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      'px-3 py-2 whitespace-nowrap',
                      col.align === 'right' && 'text-right font-mono text-xs',
                      col.align === 'center' && 'text-center'
                    )}
                  >
                    {col.format === 'status' ? (
                      <StatusBadge value={row[col.key]} />
                    ) : (
                      formatCell(row[col.key], col.format)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ value }: { value: unknown }) {
  const status = String(value ?? '');
  return (
    <Badge
      variant={status === 'proven' ? 'default' : 'destructive'}
      className="text-[10px]"
    >
      {status}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Main Widget
// ---------------------------------------------------------------------------

export function SystemObeyaWidget({ default_view }: { default_view?: string }) {
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const viewStrategy = useCapabilityStore((s) => s.strategyName('system_obeya', 'view'));

  const [data, setData] = useState<ObeyaViewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState(default_view ?? 'module_summary');

  // Matrix is the source of truth for the active view via default_view prop.
  useEffect(() => {
    if (default_view) {
      setActiveView(default_view);
    }
  }, [default_view]);

  const viewId = activeView;

  const fetchView = async (view: string) => {
    if (!viewStrategy) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.getStrategyData<ObeyaViewResponse>(viewStrategy, {
        view_name: view,
        limit: 50,
      });
      if (!result.success) {
        setError(result.error || 'Failed to load report');
        setData(null);
      } else {
        setData(result.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchView(viewId);
  }, [viewId, viewStrategy]);

  // Loading
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-10 h-10 mx-auto mb-3 text-destructive" />
          <p className="text-sm text-muted-foreground mb-3">{error}</p>
          <Button size="sm" variant="outline" onClick={() => fetchView(viewId)}>
            <RefreshCw className="w-3 h-3 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // No data
  if (!data) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Select a report from the explorer</p>
        </div>
      </div>
    );
  }

  // Dedicated dashboards for views with nested/non-tabular data
  const refreshHandler = () => fetchView(viewId);

  if (data.view === 'module_summary') {
    return <ModuleSummaryDashboard data={data} onRefresh={refreshHandler} />;
  }
  if (data.view === 'summary') {
    return <SummaryDashboard data={data} onRefresh={refreshHandler} />;
  }
  if (data.view === 'cost_breakdown') {
    return <CostBreakdownDashboard data={data} onRefresh={refreshHandler} />;
  }
  if (data.view === 'high_cc_functions') {
    return <ComplexityHotspotsDashboard data={data} onRefresh={refreshHandler} />;
  }

  // All other views get the generic table
  return <GenericTable data={data} onRefresh={refreshHandler} />;
}
